"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Upload, Send, Loader, CircleCheck, CircleAlert, Mail, BriefcaseBusiness } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inputClasses = "flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:border-secondary focus-visible:ring-secondary/30 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 md:text-sm";
const selectClasses = "flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none text-foreground placeholder:text-muted-foreground focus-visible:border-secondary focus-visible:ring-secondary/30 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 md:text-sm [&>option]:bg-background [&>option]:text-foreground";
const fileInputClasses = "flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 pr-11 text-base shadow-xs transition-[color,box-shadow] outline-none file:mr-4 file:rounded-md file:border-0 file:bg-secondary/10 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-secondary hover:file:bg-secondary/20 focus-visible:border-secondary focus-visible:ring-secondary/30 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 md:text-sm";
const interestOptions = ["Accounting and bookkeeping", "Financial management and reporting", "Year-end and audit preparation", "Program management", "Project management", "Management consulting", "Microsoft 365, SharePoint, Power BI, or reporting systems", "Economic development support", "General expression of interest"];
function ApplicationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-6 rounded-2xl border border-secondary/20 bg-secondary/10 p-8 md:p-12 text-center">
        <div className="flex size-16 items-center justify-center rounded-full bg-secondary/20 text-secondary">
          <CircleCheck className="size-8" />
        </div>

        <div className="space-y-2">
          <h3 className="text-2xl font-bold font-default text-primary-500">
            Expression of Interest Submitted
          </h3>

          <p className="text-muted-foreground font-default max-w-md">
            {successMessage}
          </p>
        </div>

        <Button onClick={resetForm} variant="outline" className="mt-2 border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white font-default">
          Submit Another Interest
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-5 md:grid-cols-2">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="font-default text-sm font-semibold text-primary-600">
                Full Name
              </FormLabel>

              <FormControl>
                <input className={inputClasses} placeholder="Example: John Smith" type="text" autoComplete="name" {...field} />
              </FormControl>

              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="font-default text-sm font-semibold text-primary-600">
                Email Address
              </FormLabel>

              <FormControl>
                <input className={inputClasses} placeholder="Example: john.smith@example.com" type="email" autoComplete="email" {...field} />
              </FormControl>

              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="font-default text-sm font-semibold text-primary-600">
                Phone Number
              </FormLabel>

              <FormControl>
                <input className={inputClasses} placeholder="Example: (807) 555-0123" type="tel" autoComplete="tel" {...field} />
              </FormControl>

              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="position" rules={{
        required: "Please select an area of interest"
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="font-default text-sm font-semibold text-primary-600">
                Area of Interest
              </FormLabel>

              <FormControl>
                <select className={selectClasses} {...field}>
                  <option value="" disabled>
                    Select an area of interest
                  </option>

                  {interestOptions.map((option, i) => <option key={i} value={option}>
                      {option}
                    </option>)}
                </select>
              </FormControl>

              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="resume" rules={{
      required: "Resume is required"
    }} render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem className="space-y-2">
            <FormLabel className="font-default text-sm font-semibold text-primary-600">
              Resume Upload
            </FormLabel>

            <FormControl>
              <div className="relative">
                <input {...field} type="file" accept=".pdf,.docx,.doc" className={fileInputClasses} onChange={e => {
            onChange(e.target.files);
          }} value={undefined} />

                <Upload className="text-muted-foreground pointer-events-none absolute right-3 top-3.5 size-5" />
              </div>
            </FormControl>

            <FormDescription className="font-default text-xs">
              Accepted formats: PDF, DOC, or DOCX. Maximum file size: 5 MB.
            </FormDescription>

            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="cover_letter" render={({
      field
    }) => <FormItem className="space-y-2">
            <FormLabel className="font-default text-sm font-semibold text-primary-600">
              Brief Note or Experience Summary
            </FormLabel>

            <FormControl>
              <Textarea placeholder="Tell us what kind of work interests you, what skills you bring, and whether you are interested in RiverBend roles, client opportunities, or both." className="min-h-[150px] font-default bg-background border-input focus-visible:border-secondary focus-visible:ring-secondary/30 resize-y" {...field} />
            </FormControl>

            <FormDescription className="font-default text-xs">
              A short note is helpful, but it does not need to be formal.
            </FormDescription>

            <FormMessage />
          </FormItem>} />

      {submitError && <div className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-destructive">
          <CircleAlert className="size-5 shrink-0" />
          <p className="text-sm font-default">{submitError}</p>
        </div>}

      <div className="flex justify-center pt-3">
        <Button type="submit" size="lg" disabled={isSubmitting} className="min-w-[240px] h-12 bg-secondary hover:bg-secondary/90 text-white font-default rounded-md">
          {isSubmitting ? <>
              <Loader className="mr-2 size-5 animate-spin" />
              Sending...
            </> : <>
              Send Expression of Interest
              <Send className="ml-2 size-5" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function QuickApplication() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="current-opportunities" className="bg-primary-500 text-primary-foreground relative overflow-hidden py-16 md:py-24">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(53,128,121,0.25),transparent_40%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.08),transparent_42%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div ref={ref} className={`transition-all duration-1000 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 xl:gap-16 items-start">
            <div className="lg:col-span-5">
              <p className="text-secondary-100 font-default font-bold tracking-widest uppercase text-sm mb-4">
                General Expression of Interest
              </p>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-white mb-6 leading-tight">
                Interested in meaningful work with RiverBend?
              </h2>

              <p className="text-primary-foreground/85 text-lg leading-relaxed font-default mb-6">
                We are always open to hearing from people who want to support
                First Nation communities, Northern organizations, businesses,
                and partnerships through practical financial management,
                reporting, systems, project support, and advisory work.
              </p>

              <p className="text-primary-foreground/85 text-lg leading-relaxed font-default mb-8">
                Sometimes that means joining RiverBend directly. Other times, it
                may mean connecting strong candidates with clients who need
                support.
              </p>

              <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-6">
                <div className="flex items-start gap-4">
                  <div className="size-12 rounded-md bg-white/10 flex items-center justify-center shrink-0">
                    <BriefcaseBusiness className="size-6 text-secondary-100" />
                  </div>

                  <div>
                    <h3 className="text-xl font-default font-bold text-white mb-2">
                      Skills we often look for
                    </h3>

                    <p className="text-primary-foreground/75 font-default leading-relaxed">
                      Accounting, bookkeeping, program management, project
                      management, management consulting, year-end preparation,
                      Microsoft 365 systems, reporting tools, and economic
                      development support.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.05] p-6">
                <div className="flex items-start gap-4">
                  <div className="size-12 rounded-md bg-white/10 flex items-center justify-center shrink-0">
                    <Mail className="size-6 text-secondary-100" />
                  </div>

                  <div>
                    <h3 className="text-xl font-default font-bold text-white mb-2">
                      Prefer email?
                    </h3>

                    <p className="text-primary-foreground/75 font-default leading-relaxed">
                      You can also send your resume and a brief note directly to{" "}
                      <a href="mailto:employment@riverbendcorp.ca?subject=Expression%20of%20Interest%20-%20RiverBend%20Opportunities" className="text-secondary-100 hover:text-white underline underline-offset-4">
                        employment@riverbendcorp.ca
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-border bg-card p-6 shadow-2xl sm:p-8 md:p-10">
                <div className="mb-8">
                  <p className="text-secondary font-default font-bold tracking-widest uppercase text-sm mb-3">
                    Send Your Information
                  </p>

                  <h3 className="text-2xl md:text-3xl font-default font-bold text-primary-500 mb-3">
                    Tell us where your skills could fit.
                  </h3>

                  <p className="text-muted-foreground font-default leading-relaxed">
                    Use this form to introduce yourself and share the type of
                    work that interests you. We review expressions of interest
                    as RiverBend and client needs arise.
                  </p>
                </div>

                <FormProvider formKey="career_application_form_team_and_careers" formId="career_application_form_team_and_careers" sectionName="careers-application" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                position: "",
                resume: undefined,
                cover_letter: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                position: {
                  required: true
                },
                resume: {
                  required: true
                }
              }} submitText="Send Expression of Interest" successMessage="Thank you for reaching out. Your information has been successfully received. Our team will review your profile and contact you if a suitable opportunity arises.">
                  <ApplicationForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}